import Link from "core-web/components/Link";
import NavigationButton from "theme/components/buttons/functionalButtons/NavigationButton";
import PropTypes from "prop-types";
import TextStyles from "theme/components/text/TextStyles";
import getQueryParams from "core-web/utils/getQueryParams";
import styled from "theme/libs/styled";
import { useTranslation } from "react-i18next";
import { withRouter } from "react-router";

const Wrapper = styled("div")`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    width: 100%;
    max-width: 660px;
`;

const NumberButtonsWrapper = styled("div")`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
`;

const PageLink = styled(Link, {
    shouldForwardProp: (prop) => ["active"].indexOf(prop) === -1,
})`
    display: grid;
    place-content: center;
    background-color: ${({ theme, active }) => (active ? theme.colors.black : theme.colors.neutral_50)};
    color: ${({ theme, active }) => (active ? theme.colors.white : theme.colors.black_50)};
    width: 36px;
    height: 36px;
    border-radius: 100%;

    ${({ theme }) => theme.above.tabletLg} {
        width: 52px;
        height: 52px;
        transition: all ${({ theme }) => theme.transitions.primary};

        :hover {
            background-color: ${({ theme, active }) => !active && theme.colors.neutral_700};
            color: ${({ theme, active }) => !active && theme.colors.white};
        }
    }

    // &[data-is-first="true"] {}
    // &[data-is-last="true"] {}
`;

const PaginationButtons = ({ current, total, handleClick, location }) => {
    const { t } = useTranslation();
    const buttons = [];

    let min, max;
    if (total <= 4) {
        min = 0;
        max = total;
    } else {
        min = current >= 3 ? current - 1 : 0;
        max = total > min + 4 ? min + 4 : total;
        if (current >= total - 2) {
            min = total - 4;
            max = total;
        }
    }

    const getQueryUrl = (pagination) => {
        pagination = pagination + 1;
        const query = getQueryParams(location);

        if (pagination === 1) {
            delete query.page;
        } else {
            query.page = pagination;
        }

        return (
            location.pathname +
            (Object.entries(query).length
                ? `?${Object.entries(query)
                      .map((q) => q.join("="))
                      .join("&")}`
                : "")
        );
    };

    if (current >= 3 && total > 4) {
        buttons.push(
            <PageLink
                data-is-first
                key="paginationStart"
                to={getQueryUrl(0)}
                onClick={handleClick && handleClick.bind(this, 0)}
            >
                <TextStyles textStyles={["Myriad Pro/20-100-semibold", null, null, null, "Myriad Pro/24-100-semibold"]}>
                    1
                </TextStyles>
            </PageLink>,
        );

        if (total !== 5) {
            buttons.push(
                <PageLink key="empty1" as="div">
                    <TextStyles
                        textStyles={["Myriad Pro/20-100-semibold", null, null, null, "Myriad Pro/24-100-semibold"]}
                    >
                        …
                    </TextStyles>
                </PageLink>,
            );
        }
    }

    for (let index = min; index < max; index++) {
        buttons.push(
            <PageLink
                key={index}
                to={getQueryUrl(index)}
                active={current === index}
                onClick={current === index ? null : handleClick && handleClick.bind(this, index)}
            >
                <TextStyles textStyles={["Myriad Pro/20-100-semibold", null, null, null, "Myriad Pro/24-100-semibold"]}>
                    {index + 1}
                </TextStyles>
            </PageLink>,
        );
    }
    if (total > max) {
        if (total !== 5) {
            buttons.push(
                <PageLink key="empty2" as="div">
                    <TextStyles
                        textStyles={["Myriad Pro/20-100-semibold", null, null, null, "Myriad Pro/24-100-semibold"]}
                    >
                        …
                    </TextStyles>
                </PageLink>,
            );
        }

        buttons.push(
            <PageLink
                data-is-last
                key="total"
                to={getQueryUrl(total - 1)}
                onClick={handleClick && handleClick.bind(this, total - 1)}
            >
                <TextStyles textStyles={["Myriad Pro/20-100-semibold", null, null, null, "Myriad Pro/24-100-semibold"]}>
                    {total}
                </TextStyles>
            </PageLink>,
        );
    }

    return (
        <Wrapper>
            <NavigationButton
                active={current > 0}
                to={current > 0 ? getQueryUrl(current - 1) : getQueryUrl(current)}
                direction="left"
                title={t("pagination.prev")}
                onClick={handleClick && handleClick.bind(this, current - 1)}
            />
            <NumberButtonsWrapper>{buttons}</NumberButtonsWrapper>
            <NavigationButton
                active={current + 1 < total}
                to={current + 1 < total ? getQueryUrl(current + 1) : getQueryUrl(current)}
                direction="right"
                title={t("pagination.next")}
                onClick={handleClick && handleClick.bind(this, current + 1)}
            />
        </Wrapper>
    );
};

PaginationButtons.propTypes = {
    current: PropTypes.number.isRequired,
    handleClick: PropTypes.func,
    location: PropTypes.object.isRequired,
    total: PropTypes.number.isRequired,
};

PaginationButtons.defaultProps = {
    handleClick: null, // (page, index) => { doSomething(page); }
};

export default withRouter(PaginationButtons);
